import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, type VerifiedCertificate } from '../api.ts';
import { formatDate } from './Certificates.tsx';

/**
 * The public certificate check (spec section 5).
 *
 * The one page in the product with no session behind it: whoever opens it is an employer holding
 * a printout, not a student. So it answers one question and shows nothing else — who, what
 * course, when, and whether it still stands. No grades, no progress, no contact details, whether
 * or not the visitor happens to be logged in.
 *
 * "Not found" is a normal answer here rather than an error state: most typos land there.
 */
export function VerifyPage(): JSX.Element {
  const { number } = useParams();
  const navigate = useNavigate();

  const [certificate, setCertificate] = useState<VerifiedCertificate | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'missing' | 'failed'>('idle');
  const [query, setQuery] = useState(number ?? '');

  useEffect(() => {
    if (!number) {
      setState('idle');
      setCertificate(null);
      return;
    }

    setState('loading');
    setQuery(number);

    api
      .verifyCertificate(number)
      .then((found) => {
        setCertificate(found);
        setState('idle');
      })
      .catch((cause: unknown) => {
        setCertificate(null);
        setState(cause instanceof ApiError && cause.status === 404 ? 'missing' : 'failed');
      });
  }, [number]);

  function search(event: FormEvent): void {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) void navigate(`/verify/${encodeURIComponent(trimmed)}`);
  }

  const expired =
    certificate?.expires_at != null && Date.parse(certificate.expires_at) < Date.now();
  const standing = certificate !== null && !certificate.is_revoked && !expired;

  return (
    <div className="verify">
      <div className="brand" style={{ fontSize: '1.3rem' }}>
        Junior<span>Prog</span>
      </div>
      <h1>Проверка сертификата</h1>
      <p className="muted">
        Введите номер с сертификата, чтобы убедиться, что он выдан и действителен.
      </p>

      <form className="row" onSubmit={search} style={{ margin: '20px 0 26px' }}>
        <input
          className="input input--inline"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Например, JP-2026-000042"
          aria-label="Номер сертификата"
        />
        <button className="btn" type="submit" disabled={query.trim().length === 0}>
          Проверить
        </button>
      </form>

      {state === 'loading' ? <p className="muted">Проверяем…</p> : null}

      {state === 'missing' ? (
        <div className="notice notice--accent">
          Сертификата с таким номером нет. Проверьте, не потерялся ли символ при переписывании.
        </div>
      ) : null}

      {state === 'failed' ? (
        <div className="notice notice--accent">
          Не удалось выполнить проверку. Попробуйте повторить позже.
        </div>
      ) : null}

      {certificate ? (
        <section className={`certcard${standing ? '' : ' certcard--void'}`}>
          <div className="certcard__head">
            <h2>{certificate.course_title}</h2>
            {certificate.is_revoked ? (
              <span className="badge">отозван</span>
            ) : expired ? (
              <span className="badge badge--wait">срок истёк</span>
            ) : (
              <span className="badge badge--live">действителен</span>
            )}
          </div>

          <div className="certcard__number">{certificate.number}</div>

          <dl className="certcard__facts">
            <div>
              <dt>Выдан</dt>
              <dd>{certificate.full_name}</dd>
            </div>
            <div>
              <dt>Кем</dt>
              <dd>{certificate.organization_name}</dd>
            </div>
            <div>
              <dt>Дата выдачи</dt>
              <dd>{formatDate(certificate.issued_at)}</dd>
            </div>
            {certificate.expires_at ? (
              <div>
                <dt>Действителен до</dt>
                <dd>{formatDate(certificate.expires_at)}</dd>
              </div>
            ) : null}
          </dl>

          {certificate.is_revoked ? (
            <div className="notice notice--accent">
              Сертификат отозван организацией и больше не подтверждает обучение.
            </div>
          ) : null}

          <p className="muted" style={{ fontSize: '0.84rem' }}>
            Страница подтверждает только факт выдачи. Оценки и содержание работ не раскрываются.
          </p>
        </section>
      ) : null}
    </div>
  );
}
